import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import applicationService from '../../services/applicationService';
import jobService from '../../services/jobService';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL;

const CandidateDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentApplications, setRecentApplications] = useState([]);
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [statsRes, applicationsRes, jobsRes, completionRes] = await Promise.all([
        applicationService.getMyStats(),
        applicationService.getMyApplications({ limit: 5 }),
        jobService.getFeaturedJobs(6),
        axios.get(`${API_URL}/candidate/profile/completion`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats(statsRes.data);
      setRecentApplications(applicationsRes.data || []);
      setRecommendedJobs(jobsRes.data || []);
      setProfileCompletion(completionRes.data.data?.percentage || 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };


  const getStatusIcon = (status) => {
    const icons = {
      pending: 'hourglass_empty',
      reviewed: 'visibility',
      shortlisted: 'star',
      interview: 'video_camera_front',
      interview_scheduled: 'video_camera_front',
      offered: 'workspace_premium',
      hired: 'check_circle',
      rejected: 'cancel'
    };
    return icons[status] || 'description';
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="candidate-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="candidate-dashboard">
      {/* Header */}
      <div className="dash-header">
        <div className="dash-header-left">
          <p className="dash-greeting">Good day 👋</p>
          <h1 className="dash-title">{user?.name || 'Candidate'}</h1>
          <p className="dash-subtitle">Here's what's happening with your job search today.</p>
        </div>

        <div className='dash-focus-img'>
          <img src="/images/dashboard-focus-image.png" alt="dashboard-focus-image" />
        </div>

      </div>

      {/* Profile Completion Alert */}
     {/*  {profileCompletion < 100 && (
        <div className="dash-alert">
          <div className="dash-alert-icon">
            <span className="material-symbols-outlined">warning</span>
          </div>
          <div className="dash-alert-text">
            <h4>Complete Your Profile</h4>
            <p>Your profile is {profileCompletion}% complete. Finish it to get better job matches!</p>
          </div>
          <Link to="/candidate/profile" className="dash-alert-btn">
            <span className="material-symbols-outlined">edit</span>
            Complete Profile
          </Link>
          <div className="dash-progress-bar">
            <div className="dash-progress-fill" style={{ width: `${profileCompletion}%` }}></div>
          </div>
        </div>
      )} */}

      {/* Statistics */}
      <div className="dash-stats">
        <div className="dash-stat-card blue">
          <div className='dash-card-top'>
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">description</span>
            </div>
          </div>
          <div className='dash-card-flashNo'>
           <div className="dash-stat-num">{stats?.totalApplications || 0}</div>
          </div>
          </div>

          <div className="dash-stat-label">Applications</div>
          <div><p className='dash-stat-small'>Your applied jobs</p></div>
        </div>

        <div className="dash-stat-card orange">
          <div className='dash-card-top'>
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">hourglass_empty</span>
            </div>
          </div>
          <div className='dash-card-flashNo'>
          <div className="dash-stat-num">{stats?.byStatus?.find(s => s._id === 'pending')?.count || 0}</div>
          </div>
          </div>

          <div className="dash-stat-label">Pending</div>
          <div><p className='dash-stat-small'>Jobs under review</p></div>
        </div>

        <div className="dash-stat-card purple">
          <div className='dash-card-top'>
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">star</span>
            </div>
          </div>
          <div className='dash-card-flashNo'>
          <div className="dash-stat-num">{stats?.byStatus?.find(s => s._id === 'shortlisted')?.count || 0}</div>
          </div>
          </div>
          <div className="dash-stat-label">Shortlisted</div>
          <div><p className='dash-stat-small'>Great opportunities</p></div>
        </div>

        <div className="dash-stat-card green">
          <div className='dash-card-top'>
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">video_camera_front</span>
            </div>
          </div>
          <div className='dash-card-flashNo'>
          <div className="dash-stat-num">{stats?.byStatus?.find(s => s._id === 'interview')?.count || 0}</div>
          </div>
          </div>
          <div className="dash-stat-label">Interviews</div>
          <div><p className='dash-stat-small'>Your upcoming interview</p></div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dash-section">
        <div className="dash-section-header">
          <h2>Quick Actions</h2>
        </div>
        <div className="dash-actions-grid">
          <Link to="/jobs" className="dash-action-card accent-blue">
            <span className="material-symbols-outlined dash-action-icon-lg">search</span>
            <div>
              <h3>Browse Jobs</h3>
              <p>Find your next opportunity</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>

          <Link to="/candidate/my-applications" className="dash-action-card accent-purple">
            <span className="material-symbols-outlined dash-action-icon-lg">description</span>
            <div>
              <h3>My Applications</h3>
              <p>Track your progress</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>

          <Link to="/candidate/profile" className="dash-action-card accent-green">
            <span className="material-symbols-outlined dash-action-icon-lg">manage_accounts</span>
            <div>
              <h3>Update Profile</h3>
              <p>Keep your info current</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>

          <Link to="/candidate/saved-jobs" className="dash-action-card accent-orange">
            <span className="material-symbols-outlined dash-action-icon-lg">bookmark</span>
            <div>
              <h3>Saved Jobs</h3>
              <p>View saved listings</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CandidateDashboard;
